import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { CheckCircle2 } from "lucide-react";
import {
  FloatingToast,
  type FloatingToastState,
  type FloatingToastTone,
} from "@/components/feedback/FloatingToast";
import { InlineLoadError } from "@/components/feedback/InlineLoadError";
import { ChangeRequestConflictModal } from "@/components/quotation/ChangeRequestConflictModal";
import {
  FinalizeOrderCta,
  FinalizeOrderDrawer,
} from "@/components/quotation/FinalizeOrderControls";
import {
  QuotationCategorySection,
  UnavailableSection,
} from "@/components/quotation/QuotationCategorySection";
import type { CurrentUser } from "@/features/auth/useCurrentUser";
import {
  canCompleteClientOrder,
  isClientStoreUser,
} from "@/features/auth/roles";
import {
  useEffectiveQuotationItems,
  useEffectiveQuotation,
  useAvailableQuotationPublications,
} from "@/features/quotation/hooks";
import {
  getActiveQuotationStoreId,
  getInitialQuotationStoreId,
  getReadyEffectiveItems,
  shouldLoadEffectiveItems,
} from "@/features/quotation/clientQuotationContext";
import { useBuyerStorePages, useStore } from "@/features/store/hooks";
import {
  useAddItem,
  useEnrichedOrderDetail,
  useOrders,
  useCreateChangeRequest,
  useUpdateOrderItem,
  useUpdateChangeRequest,
} from "@/features/order/hooks";
import {
  getOrderMutationErrorMessage,
  isOrderRequiresChangeRequestConflict,
} from "@/features/order/errors";
import { isFinancialEnrichedOrder } from "@/features/order/types";
import { useProducts } from "@/features/product/hooks";
import { ApiError } from "@/api/client";
import {
  getUserFacingErrorMessage,
  ORDER_NOT_FOUND,
} from "@/api/userFacingError";
import {
  buildClientDisplay,
  findOrderItemForQuotation,
  getEligibleOrdersForQuotation,
  getOrderTotal,
  sortDisplayItems,
} from "@/features/quotation/clientDisplay";
import { formatOrderReference } from "@/features/order/display";
import {
  getCuritibaDate,
  reconcilePublicationId,
} from "@/features/quotation/publication";
import {
  resolveDashboardQuotationLink,
  type DashboardQuotationLink,
} from "@/features/quotation/dashboardLink";
import type { DisplayItem } from "@/features/quotation/clientDisplay";
import type { Product } from "@/features/product/types";
import { formatBRL, formatDateBR } from "@/lib/timeAgo";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import {
  formatQuantityDisplay,
  getDefaultQuantity,
  isDirectKgUnit,
  isLegacyDirectKgItem,
  parsePositiveQuantityInput,
  validateWholeDirectKgQuantityInput,
} from "@/lib/quantity";
import "./QuotationWorkspace.css";
import "@/components/quotation/quotation-mock.css";
import "./quotation/QuotationListCohesion.css";

const CATEGORY_ORDER = [
  "LEGUMES",
  "FRUTAS",
  "VERDURAS",
  "FOLHAS",
  "GRAOS",
  "TEMPEROS",
  "OVOS",
  "OUTROS",
] as const;

const CATEGORY_LABELS: Record<string, string> = {
  LEGUMES: "Legumes",
  FRUTAS: "Frutas",
  VERDURAS: "Verduras",
  FOLHAS: "Folhas",
  GRAOS: "Grãos",
  TEMPEROS: "Temperos",
  OVOS: "Ovos",
  OUTROS: "Diversos",
};

const FEEDBACK_TOAST_VISIBLE_MS = 4200;
const FEEDBACK_TOAST_EXIT_MS = 180;
const DIRECT_KG_QUANTITY_TOAST = {
  title: "Quantidade inválida",
  message:
    "Para itens vendidos por kg, informe um número inteiro maior que zero.",
  tone: "error" as const,
};

const formatPublicationDate = (date: string) =>
  new Date(`${date.slice(0, 10)}T12:00:00`).toLocaleDateString("pt-BR", {
    day: "numeric",
    month: "long",
  });

type FeedbackToast = {
  title: string;
  message: string;
  tone?: FloatingToastTone;
  state: FloatingToastState;
};

function getSafeLoadErrorMessage(error: unknown, fallback: string) {
  const message = getUserFacingErrorMessage(error, fallback);
  return error instanceof ApiError && message === error.message
    ? fallback
    : message;
}

function QuotationLoadRecovery({
  title,
  message,
  onRetry,
  isRetrying = false,
}: {
  title: string;
  message: string;
  onRetry: () => void;
  isRetrying?: boolean;
}) {
  return (
    <div style={{ padding: "1.5rem", maxWidth: "800px", margin: "0 auto" }}>
      <InlineLoadError
        title={title}
        message={message}
        onRetry={onRetry}
        isRetrying={isRetrying}
      />
    </div>
  );
}

type ExpandedQuoteItemContext = {
  itemId: string;
  productId?: string;
  supplierId?: string;
};

function isUnavailableDisplayItem(
  item: DisplayItem,
): item is Extract<DisplayItem, { isUnavailable: true }> {
  return item.isUnavailable === true;
}

function getUnavailableProductId(itemId: string) {
  const prefix = "unavailable-";
  return itemId.startsWith(prefix) ? itemId.slice(prefix.length) : undefined;
}

function getExpandedQuoteItemContext(
  item: DisplayItem,
): ExpandedQuoteItemContext {
  if (isUnavailableDisplayItem(item)) {
    return {
      itemId: item.id,
      productId: getUnavailableProductId(item.id),
    };
  }
  return {
    itemId: item.id,
    productId: item.product_id,
    supplierId: item.supplier_id,
  };
}

function matchesExpandedQuoteItemContext(
  item: DisplayItem,
  context: ExpandedQuoteItemContext,
) {
  if (item.id === context.itemId) return true;
  if (isUnavailableDisplayItem(item)) {
    return Boolean(
      context.productId &&
      getUnavailableProductId(item.id) === context.productId,
    );
  }
  return Boolean(
    context.productId &&
    context.supplierId &&
    item.product_id === context.productId &&
    item.supplier_id === context.supplierId,
  );
}

const LAST_STORE_KEY = "ceasa_last_store_id";
const STORE_REQUIRED_TOAST = {
  title: "Loja não selecionada",
  message: "Selecione uma loja para adicionar este item ao pedido.",
};
const EMPTY_CART_TOAST = {
  title: "Carrinho vazio",
  message: "Adicione itens ao carrinho para finalizar um pedido.",
};
const ORDERS_LOADING_TOAST = {
  title: "Pedidos carregando",
  message: "Aguarde o carregamento dos seus pedidos.",
};
const ORDERS_ERROR_TOAST = {
  title: "Erro ao carregar pedidos",
  message: "Não foi possível carregar seus pedidos.",
};

export function ClientQuotationPage({
  currentUser,
  editOrderId,
  dashboardLink,
}: {
  currentUser: CurrentUser;
  editOrderId?: string;
  dashboardLink?: DashboardQuotationLink & { itemId?: string };
}) {
  const navigate = useNavigate();
  const isDesktop = useMediaQuery("(min-width: 768px)");
  const publicationsQuery = useAvailableQuotationPublications();
  const publications = publicationsQuery.data ?? [];
  const [selectedPublicationId, setSelectedPublicationId] = useState(
    dashboardLink?.quotationId ?? "",
  );
  const today = getCuritibaDate();
  useEffect(() => {
    if (editOrderId) return;
    setSelectedPublicationId((current) =>
      reconcilePublicationId(publications, current, today),
    );
  }, [editOrderId, publications, today]);
  const selectedPublication = publications.find(
    (publication) => publication.id === selectedPublicationId,
  );
  const selectedDate = selectedPublication?.date.slice(0, 10);
  const [selectedStoreId, setSelectedStoreId] = useState<string>(
    () => dashboardLink?.storeId ?? localStorage.getItem(LAST_STORE_KEY) ?? "",
  );
  const [manualStoreChange, setManualStoreChange] = useState(false);
  const storePages = useBuyerStorePages();
  const storesData = useMemo(
    () =>
      storePages.data
        ? { items: storePages.data.pages.flatMap((page) => page.items) }
        : undefined,
    [storePages.data],
  );
  const loadingStores = storePages.isPending;
  const linkedStore = useStore(
    dashboardLink?.storeId,
    Boolean(
      dashboardLink?.storeId &&
      storesData &&
      !storesData.items.some(({ id }) => id === dashboardLink.storeId),
    ),
  );
  const isStoreUser = isClientStoreUser(currentUser);
  const canCompleteOrders = canCompleteClientOrder(currentUser);
  const stores = useMemo(
    () =>
      [
        ...(storesData?.items ?? []),
        ...(linkedStore.data &&
        !storesData?.items.some(({ id }) => id === linkedStore.data.id)
          ? [linkedStore.data]
          : []),
      ].filter(
        (store) =>
          store.client_id === currentUser.client_id &&
          (currentUser.role !== "client_store" ||
            currentUser.store_access?.scope_type === "all" ||
            currentUser.store_access?.store_ids.includes(store.id)),
      ),
    [
      storesData,
      linkedStore.data,
      currentUser.client_id,
      currentUser.role,
      currentUser.store_access,
    ],
  );
  const selectedStoreIsAvailable = selectedStoreId
    ? stores.some((store) => store.id === selectedStoreId)
    : false;
  const activeSelectedStoreId = getActiveQuotationStoreId({
    isStoreUser,
    stores,
    selectedStoreId,
  });
  const canLoadEffectiveItems =
    shouldLoadEffectiveItems({
      isStoreUser,
      date: selectedDate,
      selectedStoreId: activeSelectedStoreId,
    }) &&
    (!dashboardLink?.storeId || selectedStoreIsAvailable);
  const dashboardLinkActive = Boolean(
    dashboardLink &&
    selectedPublicationId === dashboardLink.quotationId &&
    selectedDate === dashboardLink.date &&
    !manualStoreChange &&
    (!dashboardLink.storeId || selectedStoreId === dashboardLink.storeId),
  );
  const deepLinkedClientId =
    dashboardLinkActive && dashboardLink?.clientId === currentUser.client_id
      ? dashboardLink.clientId
      : undefined;
  const effectiveParams = {
    date: selectedDate,
    storeId: deepLinkedClientId
      ? undefined
      : activeSelectedStoreId || undefined,
    clientId: deepLinkedClientId,
  };
  const effectiveQuotationQuery = useEffectiveQuotation(effectiveParams, {
    enabled: canLoadEffectiveItems,
  });
  const effectiveItemsQuery = useEffectiveQuotationItems(effectiveParams, {
    enabled: canLoadEffectiveItems,
  });
  const {
    data: effectiveItemsResponse,
    isLoading: loadingEffectiveItems,
    isFetching: fetchingEffectiveItems,
    isPlaceholderData: placeholderEffectiveItems,
  } = effectiveItemsQuery;
  const readyEffectiveItems = getReadyEffectiveItems(
    effectiveItemsResponse,
    placeholderEffectiveItems,
  );
  const validDashboardLink =
    dashboardLinkActive &&
    dashboardLink &&
    readyEffectiveItems &&
    currentUser.client_id
      ? resolveDashboardQuotationLink(
          dashboardLink,
          publications,
          { clientId: currentUser.client_id, stores },
          effectiveQuotationQuery.data,
          readyEffectiveItems.quotation.id,
        )
      : null;
  const effectiveItemsData =
    dashboardLinkActive && !validDashboardLink
      ? undefined
      : readyEffectiveItems;
  const effectiveQuotationId =
    effectiveItemsData?.quotation.id ?? selectedPublication?.id ?? "";
  const visibleQuotation = effectiveItemsData?.quotation ?? selectedPublication;
  const items = useMemo(
    () => effectiveItemsData?.items ?? [],
    [effectiveItemsData],
  );
  const clientWideOffer = Boolean(deepLinkedClientId && validDashboardLink);
  const storeEffectiveQuery = useEffectiveQuotation(
    { date: selectedDate, storeId: activeSelectedStoreId || undefined },
    { enabled: clientWideOffer && selectedStoreIsAvailable },
  );
  const canAddClientWideItem = (itemId: string, storeId: string) =>
    !clientWideOffer ||
    (Boolean(storeId) &&
      storeId === activeSelectedStoreId &&
      selectedStoreIsAvailable &&
      !storeEffectiveQuery.isPending &&
      !storeEffectiveQuery.isFetching &&
      !storeEffectiveQuery.isError &&
      storeEffectiveQuery.data?.status === "completed" &&
      storeEffectiveQuery.data.date.slice(0, 10) === selectedDate &&
      storeEffectiveQuery.data.id ===
        items.find((item) => item.id === itemId)?.quotation_id);
  const clientWideOfferHint = !clientWideOffer
    ? undefined
    : !activeSelectedStoreId
      ? "Selecione uma loja para adicionar."
      : storeEffectiveQuery.isPending || storeEffectiveQuery.isFetching
        ? "Verificando cotação da loja…"
        : items.some((item) =>
              canAddClientWideItem(item.id, activeSelectedStoreId),
            )
          ? undefined
          : "Oferta indisponível nesta loja. Escolha outra loja.";
  const quoteTransitionPending =
    placeholderEffectiveItems ||
    (fetchingEffectiveItems && !loadingEffectiveItems);
  const { data: productsData } = useProducts();
  const {
    data: ordersData,
    error: ordersListError,
    isLoading: loadingOrdersList,
  } = useOrders();
  const {
    data: requestedEditOrder,
    error: editOrderError,
    isLoading: loadingEditOrder,
    isFetching: fetchingEditOrder,
    refetch: refetchEditOrder,
  } = useEnrichedOrderDetail(editOrderId);
  const ordersError = ordersListError ?? editOrderError;
  const loadingOrders =
    loadingOrdersList || (Boolean(editOrderId) && loadingEditOrder);
  const addItemMutation = useAddItem();
  const updateOrderItemMutation = useUpdateOrderItem();
  const createCR = useCreateChangeRequest();
  const updateCR = useUpdateChangeRequest();

  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    new Set(["__offers", "__unavailable", ...CATEGORY_ORDER]),
  );
  const [expandedUnavailableGroups, setExpandedUnavailableGroups] = useState<
    Set<string>
  >(new Set(CATEGORY_ORDER));
  const [expandedItemContext, setExpandedItemContext] =
    useState<ExpandedQuoteItemContext | null>(null);
  const [dismissedDeepLinkItem, setDismissedDeepLinkItem] = useState<
    string | null
  >(null);
  const [quantity, setQuantity] = useState("1");
  const [addSuccess, setAddSuccess] = useState<string | null>(null);
  const [addError, setAddError] = useState<string | null>(null);
  const [feedbackToast, setFeedbackToast] = useState<FeedbackToast | null>(
    null,
  );
  const [showCRWarning, setShowCRWarning] = useState<{
    quotationId: string;
    storeId: string;
    orderId: string;
    pendingChangeRequestId?: string;
    quotationItemId: string;
    quantity: number;
  } | null>(null);
  const changeRequestAllowed =
    !showCRWarning ||
    (showCRWarning.storeId === activeSelectedStoreId &&
      canAddClientWideItem(
        showCRWarning.quotationItemId,
        showCRWarning.storeId,
      ));
  const [crError, setCrError] = useState<string | null>(null);
  const [showFinalizeSelector, setShowFinalizeSelector] = useState(false);

  useEffect(() => {
    if (!feedbackToast) return;
    const timeout = window.setTimeout(
      () => {
        if (feedbackToast.state === "exiting") {
          setFeedbackToast(null);
          return;
        }
        setFeedbackToast((current) =>
          current ? { ...current, state: "exiting" } : current,
        );
      },
      feedbackToast.state === "exiting"
        ? FEEDBACK_TOAST_EXIT_MS
        : FEEDBACK_TOAST_VISIBLE_MS,
    );
    return () => window.clearTimeout(timeout);
  }, [feedbackToast]);

  useEffect(() => {
    if (!storesData) return;
    if (dashboardLink?.storeId === selectedStoreId && linkedStore.isPending)
      return;
    if (isStoreUser) {
      const nextStoreId = getInitialQuotationStoreId(stores, selectedStoreId);
      if (nextStoreId === selectedStoreId) return;
      if (nextStoreId) {
        localStorage.setItem(LAST_STORE_KEY, nextStoreId);
      } else {
        localStorage.removeItem(LAST_STORE_KEY);
      }
      setSelectedStoreId(nextStoreId);
      return;
    }
    if (stores.length === 1) {
      const nextStoreId = stores[0]!.id;
      if (nextStoreId === selectedStoreId) return;
      localStorage.setItem(LAST_STORE_KEY, nextStoreId);
      setSelectedStoreId(nextStoreId);
      return;
    }
    if (!selectedStoreId || selectedStoreIsAvailable) return;
    localStorage.removeItem(LAST_STORE_KEY);
    setSelectedStoreId("");
  }, [
    isStoreUser,
    selectedStoreId,
    selectedStoreIsAvailable,
    stores,
    storesData,
    dashboardLink?.storeId,
    linkedStore.isPending,
  ]);

  function getLineTotalLimitMessage(message: string) {
    return message.includes("R$ 1.000.000.000,00") ? message : null;
  }

  function showFeedbackToast(toast: Omit<FeedbackToast, "state">) {
    setAddError(null);
    setFeedbackToast({ ...toast, state: "visible" });
  }

  function dismissFeedbackToast() {
    setFeedbackToast((current) =>
      current ? { ...current, state: "exiting" } : current,
    );
  }

  function handleStoreChange(storeId: string) {
    setShowCRWarning(null);
    setManualStoreChange(true);
    setSelectedStoreId(storeId);
    if (storeId) {
      localStorage.setItem(LAST_STORE_KEY, storeId);
    } else {
      localStorage.removeItem(LAST_STORE_KEY);
    }
    setAddError(null);
    setAddSuccess(null);
    dismissFeedbackToast();
  }

  function toggleCategory(cat: string) {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat);
      else next.add(cat);
      return next;
    });
  }

  function handleExpandItem(itemId: string) {
    if (
      itemId === dashboardLink?.itemId &&
      validDashboardLink &&
      !expandedItemContext &&
      dismissedDeepLinkItem !== itemId
    ) {
      setDismissedDeepLinkItem(itemId);
      return;
    }
    setDismissedDeepLinkItem(dashboardLink?.itemId ?? null);
    const displayItem = displayItems.find((entry) => entry.id === itemId);
    if (!displayItem) {
      setExpandedItemContext(null);
      return;
    }
    const nextContext = getExpandedQuoteItemContext(displayItem);
    setExpandedItemContext((prev) =>
      prev && matchesExpandedQuoteItemContext(displayItem, prev)
        ? null
        : nextContext,
    );
    setAddError(null);
    const item = items.find((entry) => entry.id === itemId);
    const currentOrderItem = editingOrder
      ? findOrderItemForQuotation(editingOrder, itemId)
      : undefined;
    if (currentOrderItem) {
      setQuantity(String(currentOrderItem.quantity));
      return;
    }
    if (!isDirectKgUnit(item?.unit_type)) {
      setQuantity(String(getDefaultQuantity(item?.unit_type)));
    }
  }

  async function handleAddItem(quotationItemId: string) {
    if (!canAddClientWideItem(quotationItemId, activeSelectedStoreId)) return;
    if (!activeSelectedStoreId) {
      showFeedbackToast(STORE_REQUIRED_TOAST);
      return;
    }
    const activeOrder = ordersData?.items.find(
      (order) =>
        order.store_id === activeSelectedStoreId &&
        order.quotation_id === effectiveQuotationId,
    );
    if (activeOrder?.items.some(isLegacyDirectKgItem)) {
      showFeedbackToast({
        title: "Registro histórico",
        message: "Registro histórico — somente visualização.",
      });
      return;
    }
    setAddError(null);
    dismissFeedbackToast();
    const selectedItem = items.find((entry) => entry.id === quotationItemId);
    const directKgValidation = isDirectKgUnit(selectedItem?.unit_type)
      ? validateWholeDirectKgQuantityInput(quantity)
      : null;
    if (directKgValidation?.reason) {
      showFeedbackToast(DIRECT_KG_QUANTITY_TOAST);
      return;
    }
    const parsedQuantity = directKgValidation
      ? directKgValidation.quantity!
      : (parsePositiveQuantityInput(quantity, selectedItem?.unit_type) ??
        getDefaultQuantity(selectedItem?.unit_type));
    const currentOrderItem = editingOrder
      ? findOrderItemForQuotation(editingOrder, quotationItemId)
      : undefined;
    try {
      if (currentOrderItem && editingOrder) {
        await updateOrderItemMutation.mutateAsync({
          orderId: editingOrder.id,
          itemId: currentOrderItem.id,
          payload: { quantity: parsedQuantity },
        });
        setExpandedItemContext(null);
        setAddSuccess(quotationItemId);
        setTimeout(() => setAddSuccess(null), 2000);
        return;
      }
      await addItemMutation.mutateAsync({
        store_id: activeSelectedStoreId,
        quotation_item_id: quotationItemId,
        quantity: parsedQuantity,
      });
      localStorage.setItem(LAST_STORE_KEY, activeSelectedStoreId);
      setExpandedItemContext(null);
      setAddSuccess(quotationItemId);
      setTimeout(() => setAddSuccess(null), 2000);
    } catch (e) {
      if (isOrderRequiresChangeRequestConflict(e)) {
        if (!activeOrder) {
          setAddError(ORDER_NOT_FOUND);
          return;
        }
        setExpandedItemContext(null);
        setCrError(null);
        setShowCRWarning({
          quotationId: effectiveQuotationId,
          storeId: activeSelectedStoreId,
          orderId: activeOrder.id,
          pendingChangeRequestId: activeOrder.pending_change_request_id,
          quotationItemId,
          quantity: parsedQuantity,
        });
      } else {
        const message = getOrderMutationErrorMessage(e);
        const limitMessage = getLineTotalLimitMessage(message);
        if (limitMessage) {
          showFeedbackToast({
            title: "Limite do item atingido",
            message: limitMessage,
          });
        } else {
          setAddError(message);
        }
      }
    }
  }

  async function handleConfirmCR() {
    if (!showCRWarning) return;
    if (!changeRequestAllowed) return;
    setCrError(null);
    const addPayload = {
      changes_payload: {
        add: [
          {
            quotation_item_id: showCRWarning.quotationItemId,
            quantity: showCRWarning.quantity,
          },
        ],
      },
    };
    try {
      if (showCRWarning.pendingChangeRequestId) {
        await updateCR.mutateAsync({
          crId: showCRWarning.pendingChangeRequestId,
          payload: addPayload,
        });
      } else {
        await createCR.mutateAsync({
          orderId: showCRWarning.orderId,
          payload: addPayload,
        });
      }
      setShowCRWarning(null);
      setAddSuccess(showCRWarning.quotationItemId);
      setTimeout(() => setAddSuccess(null), 2000);
    } catch (e) {
      const message = getOrderMutationErrorMessage(e);
      const limitMessage = getLineTotalLimitMessage(message);
      if (limitMessage) {
        setCrError(null);
        showFeedbackToast({
          title: "Limite do item atingido",
          message: limitMessage,
        });
      } else {
        setCrError(message);
      }
    }
  }

  function goToPedido(orderId: string) {
    setShowFinalizeSelector(false);
    navigate({
      to: "/pedidos",
      search: { orderId, fromCart: "1" },
    });
  }

  const allProducts: Product[] = useMemo(
    () => productsData?.items ?? [],
    [productsData],
  );
  const orders = useMemo(() => ordersData?.items ?? [], [ordersData]);
  const financialOrders = useMemo(
    () => orders.filter(isFinancialEnrichedOrder),
    [orders],
  );
  const editingOrder = useMemo(
    () =>
      requestedEditOrder &&
      isFinancialEnrichedOrder(requestedEditOrder) &&
      requestedEditOrder.id === editOrderId &&
      requestedEditOrder.status === "open"
        ? requestedEditOrder
        : undefined,
    [editOrderId, requestedEditOrder],
  );
  useEffect(() => {
    if (!editingOrder) return;
    setSelectedStoreId(editingOrder.store_id);
    setSelectedPublicationId(editingOrder.quotation_id);
    localStorage.setItem(LAST_STORE_KEY, editingOrder.store_id);
  }, [editingOrder]);
  const selectableStores = editingOrder
    ? stores.filter(({ id }) => id === editingOrder.store_id)
    : stores;
  const editingPublicationAvailable = Boolean(
    editingOrder &&
    publications.some(({ id }) => id === editingOrder.quotation_id),
  );
  const activeOrderHasLegacyDirectKg = editingOrder
    ? editingOrder.items.some(isLegacyDirectKgItem)
    : orders.some(
        (order) =>
          order.store_id === activeSelectedStoreId &&
          order.quotation_id === effectiveQuotationId &&
          order.items.some(isLegacyDirectKgItem),
      );
  const storeNameById = useMemo(
    () => new Map(stores.map((store) => [store.id, store.name])),
    [stores],
  );
  const eligibleOrders = useMemo(
    () => getEligibleOrdersForQuotation(financialOrders, effectiveQuotationId),
    [financialOrders, effectiveQuotationId],
  );
  const eligibleTotal = useMemo(
    () => eligibleOrders.reduce((sum, order) => sum + getOrderTotal(order), 0),
    [eligibleOrders],
  );
  const hasEligibleOrders = eligibleOrders.length > 0;
  const finalizeDisabled =
    quoteTransitionPending ||
    !effectiveQuotationId ||
    loadingOrders ||
    Boolean(ordersError) ||
    !hasEligibleOrders;
  const finalizeStoreLabel = hasEligibleOrders
    ? `${eligibleOrders.length} ${eligibleOrders.length === 1 ? "loja" : "lojas"}`
    : undefined;
  const finalizeTotalLabel = hasEligibleOrders
    ? formatBRL(eligibleTotal)
    : undefined;
  const finalizeStatusLabel = ordersError
    ? ORDERS_ERROR_TOAST.title
    : loadingOrders
      ? ORDERS_LOADING_TOAST.title
      : hasEligibleOrders
        ? undefined
        : EMPTY_CART_TOAST.title;
  const finalizeSelectorOpen =
    showFinalizeSelector && eligibleOrders.length > 1;

  const clientDisplay = useMemo(
    () => buildClientDisplay(items, allProducts),
    [items, allProducts],
  );
  const grouped = clientDisplay.grouped;
  const offers = clientDisplay.offers;
  const unavailableGroups = CATEGORY_ORDER.map((category) => ({
    category,
    label: CATEGORY_LABELS[category] ?? category,
    items: (grouped[category] ?? []).filter((item) => item.isUnavailable),
  }));
  const displayItems = useMemo(
    () => [
      ...offers.slice().sort(sortDisplayItems),
      ...CATEGORY_ORDER.flatMap((category) =>
        (grouped[category] ?? []).slice().sort(sortDisplayItems),
      ),
    ],
    [grouped, offers],
  );
  const expandedItemId = useMemo(() => {
    if (!expandedItemContext)
      return dashboardLinkActive &&
        validDashboardLink &&
        dismissedDeepLinkItem !== dashboardLink?.itemId &&
        items.some((item) => item.id === dashboardLink?.itemId && item.is_offer)
        ? dashboardLink!.itemId!
        : null;
    return (
      displayItems.find((item) =>
        matchesExpandedQuoteItemContext(item, expandedItemContext),
      )?.id ?? null
    );
  }, [
    displayItems,
    expandedItemContext,
    validDashboardLink,
    dashboardLinkActive,
    dismissedDeepLinkItem,
    items,
    dashboardLink,
  ]);
  const hasDisplayItems = displayItems.length > 0;
  const effectiveItemsUnavailable =
    !publicationsQuery.isError &&
    canLoadEffectiveItems &&
    effectiveItemsQuery.isError &&
    !effectiveItemsData;

  if (
    publicationsQuery.isLoading ||
    (Boolean(editOrderId) && loadingEditOrder) ||
    (isStoreUser && loadingStores && !storesData) ||
    (selectedPublicationId && loadingEffectiveItems && !effectiveItemsData)
  ) {
    return (
      <div style={{ padding: "1.5rem", maxWidth: "800px", margin: "0 auto" }}>
        <p
          style={{
            fontFamily: "'DM Sans', sans-serif",
            color: "var(--login-muted)",
            fontSize: "0.875rem",
          }}
        >
          Carregando...
        </p>
      </div>
    );
  }

  if (editOrderId && editOrderError) {
    return (
      <QuotationLoadRecovery
        title="Pedido indisponível"
        message={getSafeLoadErrorMessage(
          editOrderError,
          "Não foi possível carregar o pedido para edição.",
        )}
        onRetry={() => void refetchEditOrder()}
        isRetrying={fetchingEditOrder}
      />
    );
  }

  if (
    editOrderId &&
    requestedEditOrder &&
    (!editingOrder || !editingPublicationAvailable)
  ) {
    return (
      <div style={{ padding: "1.5rem", maxWidth: "800px", margin: "0 auto" }}>
        <p>Este pedido não está disponível para edição.</p>
        <button type="button" onClick={() => navigate({ to: "/pedidos" })}>
          Voltar aos pedidos
        </button>
      </div>
    );
  }

  if (publicationsQuery.isError && publications.length === 0) {
    return (
      <QuotationLoadRecovery
        title="Cotações indisponíveis"
        message={getSafeLoadErrorMessage(
          publicationsQuery.error,
          "Não foi possível carregar as cotações disponíveis.",
        )}
        onRetry={() => void publicationsQuery.refetch()}
        isRetrying={publicationsQuery.isFetching}
      />
    );
  }

  if (effectiveItemsUnavailable) {
    return (
      <QuotationLoadRecovery
        title="Itens da cotação indisponíveis"
        message={getSafeLoadErrorMessage(
          effectiveItemsQuery.error,
          "Não foi possível carregar os itens da cotação.",
        )}
        onRetry={() => void effectiveItemsQuery.refetch()}
        isRetrying={effectiveItemsQuery.isFetching}
      />
    );
  }

  if (!selectedPublication) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "calc(100vh - 3.5rem - 4rem)",
          gap: "0.75rem",
          maxWidth: "800px",
          margin: "0 auto",
        }}
      >
        <p
          style={{
            fontFamily: "'Barlow Condensed', sans-serif",
            fontWeight: 700,
            textTransform: "uppercase",
            fontSize: "1.25rem",
            color: "var(--login-muted)",
            letterSpacing: "0.05em",
          }}
        >
          Nenhuma cotação disponível
        </p>
        <p
          style={{
            fontFamily: "'DM Sans', sans-serif",
            fontSize: "0.875rem",
            color: "var(--login-muted)",
          }}
        >
          Aguarde uma cotação ser publicada para você.
        </p>
      </div>
    );
  }

  return (
    <>
      <style>{`
        @keyframes fadeSlideIn { from { opacity: 0; transform: translateY(-6px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes modalIn { from { opacity: 0; transform: translate(-50%, -50%) scale(0.96) translateY(8px); } to { opacity: 1; transform: translate(-50%, -50%) scale(1) translateY(0); } }
        .cat-header { transition: filter 0.15s; }
        .cat-header:hover { filter: brightness(0.95); }
        .finalize-selector-row { transition: border-color 0.16s ease, background 0.16s ease, opacity 0.16s ease; }
        .finalize-selector-row:hover { opacity: 0.92; border-color: color-mix(in srgb, var(--login-accent) 42%, var(--login-border)); background: color-mix(in srgb, var(--login-accent) 9%, var(--login-surface)); }
        .finalize-selector-row:active { opacity: 0.98; }
        .cotacao-category-items { animation: fadeSlideIn 0.14s ease both; }
        .cotacao-product-card {
          transition: border-color 0.16s ease, transform 0.16s ease, box-shadow 0.16s ease, background 0.16s ease;
          will-change: transform;
        }
        .cotacao-product-card:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px color-mix(in srgb, var(--login-text) 8%, transparent);
        }
        .cotacao-unavailable-card:hover {
          transform: none;
          box-shadow: none;
        }
        .finalize-cta-button { transition: transform 0.16s ease, opacity 0.16s ease, filter 0.16s ease, box-shadow 0.16s ease; }
        .finalize-cta-button:hover { opacity: 0.94; filter: brightness(0.98); transform: translateY(-1px); }
        .finalize-cta-button:active { opacity: 0.9; transform: translateY(0); }
        @media (prefers-reduced-motion: reduce) {
          .cotacao-category-items,
          .cotacao-product-card,
          .finalize-selector-row,
          .finalize-cta-button {
            animation: none !important;
            transition: none !important;
          }
          .cotacao-product-card:hover,
          .finalize-cta-button:hover,
          .finalize-cta-button:active {
            transform: none;
          }
        }
      `}</style>

      {feedbackToast ? (
        <FloatingToast
          title={feedbackToast.title}
          message={feedbackToast.message}
          tone={feedbackToast.tone}
          state={feedbackToast.state}
          isDesktop={isDesktop}
        />
      ) : null}

      <ChangeRequestConflictModal
        open={Boolean(showCRWarning)}
        error={
          crError ??
          (!changeRequestAllowed
            ? (clientWideOfferHint ??
              "Oferta indisponível nesta loja. Escolha outra loja.")
            : null)
        }
        confirmDisabled={!changeRequestAllowed}
        isPending={createCR.isPending || updateCR.isPending}
        onCancel={() => setShowCRWarning(null)}
        onConfirm={handleConfirmCR}
      />

      {canCompleteOrders && (
        <FinalizeOrderDrawer
          open={finalizeSelectorOpen}
          orders={eligibleOrders}
          storeNameById={storeNameById}
          onClose={() => setShowFinalizeSelector(false)}
          onOpenOrder={goToPedido}
        />
      )}

      <div
        data-testid="cotacao-content"
        className="quotation-client-page lnk-page app-page-frame"
      >
        {editingOrder ? (
          <section
            className="quotation-edit-context"
            aria-label="Pedido em edição"
          >
            <div>
              <strong>
                Editando pedido {formatOrderReference(editingOrder.id)}
              </strong>
              <span>{editingOrder.store_name}</span>
            </div>
            <button
              type="button"
              onClick={() =>
                navigate({
                  to: "/pedidos",
                  search: { orderId: editingOrder.id },
                })
              }
            >
              Voltar ao pedido
            </button>
          </section>
        ) : null}
        <header className="lnk-page-header">
          <div>
            <h1>
              {visibleQuotation
                ? `${new Date(`${visibleQuotation.date.slice(0, 10)}T12:00:00`).toLocaleDateString("pt-BR", { weekday: "short" }).replace(".", "").toUpperCase()}, ${formatPublicationDate(visibleQuotation.date)}`
                : "—"}
            </h1>
          </div>
          <div className="lnk-date-state">
            <span>
              Atualizada às{" "}
              {visibleQuotation?.updated_at
                ? new Date(visibleQuotation.updated_at).toLocaleTimeString(
                    "pt-BR",
                    {
                      hour: "2-digit",
                      minute: "2-digit",
                      timeZone: "America/Sao_Paulo",
                    },
                  )
                : "—"}
            </span>
            <strong>Aberta para pedidos</strong>
          </div>
        </header>
        {!editingOrder && publications.length > 1 && (
          <section
            className={`lnk-day-selector quotation-day-selector ${publications.length <= 3 ? "is-compact" : ""}`}
            role="region"
            aria-label="Cotações disponíveis"
          >
            <header>
              <span className="lnk-kicker">COTAÇÕES DISPONÍVEIS</span>
              <strong>Escolha o dia</strong>
            </header>
            <div>
              {publications.map((publication) => {
                const date = new Date(
                  `${publication.date.slice(0, 10)}T12:00:00`,
                );
                const selected = publication.id === selectedPublicationId;
                return (
                  <button
                    key={publication.id}
                    type="button"
                    className={selected ? "active" : ""}
                    aria-pressed={selected}
                    aria-label={`${publication.date.slice(0, 10) === today ? "Hoje, " : ""}${formatPublicationDate(publication.date)}${selected ? ", selecionada" : ""}`}
                    onClick={() => setSelectedPublicationId(publication.id)}
                  >
                    <small>
                      {date
                        .toLocaleDateString("pt-BR", { weekday: "short" })
                        .replace(".", "")
                        .toUpperCase()}
                    </small>
                    <strong>
                      {date.toLocaleDateString("pt-BR", { day: "2-digit" })}
                    </strong>
                    <span>
                      {date
                        .toLocaleDateString("pt-BR", { month: "short" })
                        .replace(".", "")}
                    </span>
                    {selected && <CheckCircle2 size={16} />}
                  </button>
                );
              })}
            </div>
          </section>
        )}

        {isStoreUser && storesData && stores.length === 0 ? (
          <p
            style={{
              fontFamily: "'DM Sans', sans-serif",
              color: "var(--login-muted)",
              fontSize: "0.875rem",
            }}
          >
            Nenhuma loja vinculada ao seu usuário.
          </p>
        ) : !hasDisplayItems ? (
          <p
            style={{
              fontFamily: "'DM Sans', sans-serif",
              color: "var(--login-muted)",
              fontSize: "0.875rem",
            }}
          >
            Nenhum item na cotação.
          </p>
        ) : null}

        {storePages.hasNextPage && (
          <button
            type="button"
            disabled={storePages.isFetchingNextPage}
            onClick={() => void storePages.fetchNextPage()}
          >
            {storePages.isFetchingNextPage
              ? "Carregando lojas…"
              : "Carregar mais lojas"}
          </button>
        )}

        <div>
          {activeOrderHasLegacyDirectKg && (
            <p
              style={{
                margin: 0,
                color: "var(--login-muted)",
                fontFamily: "'DM Sans', sans-serif",
                fontSize: "0.875rem",
              }}
            >
              Registro histórico — somente visualização.
            </p>
          )}
          {offers.length > 0 ? (
            <QuotationCategorySection
              category="__offers"
              label="Ofertas"
              variant="offers"
              items={offers}
              expanded={expandedCategories.has("__offers")}
              expandedItemId={expandedItemId}
              selectedStoreId={activeSelectedStoreId}
              stores={selectableStores}
              quantity={quantity}
              addError={addError}
              addSuccessId={addSuccess}
              isAdding={
                addItemMutation.isPending || updateOrderItemMutation.isPending
              }
              addDisabled={quoteTransitionPending}
              confirmDisabled={Boolean(clientWideOfferHint)}
              addHint={clientWideOfferHint}
              addReadOnly={activeOrderHasLegacyDirectKg}
              onToggleCategory={toggleCategory}
              onExpandItem={handleExpandItem}
              onStoreChange={handleStoreChange}
              onQuantityChange={setQuantity}
              onQuantityBlur={(item) => {
                if (isDirectKgUnit(item.unit_type)) return;
                const nextQuantity =
                  parsePositiveQuantityInput(quantity, item.unit_type) ??
                  getDefaultQuantity(item.unit_type);
                setQuantity(
                  formatQuantityDisplay(nextQuantity, item.unit_type),
                );
              }}
              onConfirmAdd={handleAddItem}
              getItemActionLabel={(itemId) =>
                editingOrder && findOrderItemForQuotation(editingOrder, itemId)
                  ? "Atualizar"
                  : "Adicionar"
              }
            />
          ) : null}
          <section className="lnk-catalog-section">
            <div className="lnk-section-heading">
              <div>
                <span className="lnk-kicker">CATÁLOGO COMPLETO</span>
                <h2>Todos os produtos</h2>
              </div>
            </div>
            <div className="lnk-catalog-grid">
              {CATEGORY_ORDER.filter(
                (cat) =>
                  grouped[cat]?.some((item) => !item.isUnavailable) ?? false,
              ).map((cat) => {
                const catItems = grouped[cat]!.slice().sort(sortDisplayItems);
                const expanded = expandedCategories.has(cat);

                return (
                  <QuotationCategorySection
                    key={cat}
                    category={cat}
                    label={CATEGORY_LABELS[cat] ?? cat}
                    items={catItems.filter((item) => !item.isUnavailable)}
                    expanded={expanded}
                    expandedItemId={expandedItemId}
                    selectedStoreId={activeSelectedStoreId}
                    stores={selectableStores}
                    quantity={quantity}
                    addError={addError}
                    addSuccessId={addSuccess}
                    isAdding={
                      addItemMutation.isPending ||
                      updateOrderItemMutation.isPending
                    }
                    addDisabled={quoteTransitionPending}
                    confirmDisabled={Boolean(clientWideOfferHint)}
                    addHint={clientWideOfferHint}
                    addReadOnly={activeOrderHasLegacyDirectKg}
                    onToggleCategory={toggleCategory}
                    onExpandItem={handleExpandItem}
                    onStoreChange={(storeId) => {
                      handleStoreChange(storeId);
                    }}
                    onQuantityChange={setQuantity}
                    onQuantityBlur={(item) => {
                      if (isDirectKgUnit(item.unit_type)) return;
                      const nextQuantity =
                        parsePositiveQuantityInput(quantity, item.unit_type) ??
                        getDefaultQuantity(item.unit_type);
                      setQuantity(
                        formatQuantityDisplay(nextQuantity, item.unit_type),
                      );
                    }}
                    onConfirmAdd={handleAddItem}
                    getItemActionLabel={(itemId) =>
                      editingOrder &&
                      findOrderItemForQuotation(editingOrder, itemId)
                        ? "Atualizar"
                        : "Adicionar"
                    }
                  />
                );
              })}
              <UnavailableSection
                groups={unavailableGroups}
                dateLabel={
                  selectedDate && selectedDate !== today
                    ? formatDateBR(selectedDate)
                    : undefined
                }
                expanded={expandedCategories.has("__unavailable")}
                onToggle={() => toggleCategory("__unavailable")}
                openGroups={expandedUnavailableGroups}
                onToggleGroup={(category) =>
                  setExpandedUnavailableGroups((previous) => {
                    const next = new Set(previous);
                    if (next.has(category)) next.delete(category);
                    else next.add(category);
                    return next;
                  })
                }
              />
            </div>
          </section>
        </div>
      </div>

      {canCompleteOrders && (
        <FinalizeOrderCta
          disabled={finalizeDisabled}
          storeLabel={finalizeStoreLabel}
          totalLabel={finalizeTotalLabel}
          statusLabel={finalizeStatusLabel}
          dateLabel={
            visibleQuotation
              ? formatPublicationDate(visibleQuotation.date)
              : undefined
          }
          onClick={() => {
            if (ordersError) {
              showFeedbackToast(ORDERS_ERROR_TOAST);
              return;
            }
            if (loadingOrders) {
              showFeedbackToast(ORDERS_LOADING_TOAST);
              return;
            }
            if (!hasEligibleOrders) {
              showFeedbackToast(EMPTY_CART_TOAST);
              return;
            }
            if (eligibleOrders.length === 1) {
              goToPedido(eligibleOrders[0]!.id);
              return;
            }
            setShowFinalizeSelector(true);
          }}
        />
      )}
    </>
  );
}
